import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { LibCoreLayoutModule } from '../../core-layout';
import {
  AvailableSlots,
  CALENDAR_VIEW_MODE,
  CalendarView,
  DATE_FORMAT,
  Product,
  TimeSlots,
  VALUE_CHANGES_DEBOUNCE,
} from '../../../models';
import { BaseComponent } from '../../../core';
import {
  BusinessObjectUtils,
  ComparatorUtils,
  DateTimeUtils,
} from '../../../utils';
import { CalendarSlotButtonComponent } from '../calendar-slot-button/calendar-slot-button.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CalendarDatePickerComponent } from '../calendar-date-picker/calendar-date-picker.component';

@Component({
  selector: 'lib-calendar-available-slot-selection-by-date',
  templateUrl: './calendar-available-slot-selection-by-date.component.html',
  styleUrls: ['./calendar-available-slot-selection-by-date.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    CalendarSlotButtonComponent,
    CalendarDatePickerComponent,
    // material design
    MatProgressBarModule,
  ],
})
export class CalendarAvailableSlotSelectionByDateComponent extends BaseComponent {
  public readonly SCROLLING_SLOT_CONTAINER_ID = 'scrolling-time-slot-container';

  @Input() isLoading: boolean;
  @Input() minDate: Date;
  @Input() maxDate: Date;

  private _availableSlots: AvailableSlots;
  @Input() set availableSlots(value: AvailableSlots) {
    this._availableSlots = value;

    if (value) {
      // order is important
      const currentCalendarView: CalendarView = this.controlSelectedDate.value;

      const newDate = DateTimeUtils.parseStringToDate(value.from);

      if (!DateTimeUtils.isSameDate(currentCalendarView?.fromDate, newDate)) {
        this.controlSelectedDate.setValue({
          viewMode: currentCalendarView?.viewMode,
          fromDate: newDate,
          toDate: newDate,
        });
      }

      this.splitSlotByDayTime(value);
    }
  }
  get availableSlots(): AvailableSlots {
    return this._availableSlots;
  }

  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.controlSelectedDate.disable();
    } else {
      this.controlSelectedDate.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() selectedProducts: Product[];

  @Input() set presetSelectedSlot(value: Date) {
    this.selectSlotChanged(value);
  }

  @Output() selectedDate = new EventEmitter<Date>(); // emit to trigger fetching available slots
  @Output() selectedSlot = new EventEmitter<Date>();

  public morningSlots: Date[] = [];
  public afternoonSlots: Date[] = [];
  public eveningSlots: Date[] = [];

  public controlSelectedDate = new FormControl<CalendarView>(
    {
      viewMode: CALENDAR_VIEW_MODE.DAY,
      fromDate: null,
      toDate: null,
    },
    [Validators.required]
  );

  private readonly AFTERNOON_START_TIME = '12:00:00';
  private readonly EVENING_START_TIME = '17:00:00';

  private currentSelectedSlot: Date;

  constructor() {
    super();
    this.controlSelectedDate.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(ComparatorUtils.generateComparatorForObject()),
        debounceTime(VALUE_CHANGES_DEBOUNCE)
      )
      .subscribe((v) => {
        this.selectedDate.emit(v?.fromDate);
      });
  }

  public generateHtmlIdForSlot(slot: Date): string {
    return DateTimeUtils.formatDateTimeToString(
      slot,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
  }

  public isSlotSelected(d: Date): boolean {
    return (
      DateTimeUtils.isSameDate(d, this.currentSelectedSlot) &&
      DateTimeUtils.isSameTime(d, this.currentSelectedSlot, true)
    );
  }

  public isAnyDiscountInSlot(d: Date): boolean {
    return (
      this.selectedProducts?.findIndex((p) =>
        BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(p, d)
      ) >= 0
    );
  }

  public selectSlotChanged(d: Date): void {
    this.currentSelectedSlot = d;
    this.selectedSlot.emit(d);
    this.scrollToSelectedTimeSlot();
  }

  private splitSlotByDayTime(availableSlots: AvailableSlots): void {
    this.morningSlots = [];
    this.afternoonSlots = [];
    this.eveningSlots = [];

    const timeSlots: TimeSlots = availableSlots?.slotsOfDates?.find(
      (slotOfDate) =>
        DateTimeUtils.formatDateToString(
          this.controlSelectedDate.value?.fromDate,
          DATE_FORMAT.DATE_FORMAT_BACKEND
        ) === slotOfDate.date
    );

    timeSlots?.times?.forEach((t) => {
      const slotAsDate = DateTimeUtils.generateDateFromDateAndTimeString(
        timeSlots.date,
        t
      );

      if (t >= this.EVENING_START_TIME) {
        this.eveningSlots.push(slotAsDate);
      } else if (t >= this.AFTERNOON_START_TIME) {
        this.afternoonSlots.push(slotAsDate);
      } else {
        this.morningSlots.push(slotAsDate);
      }
    });

    this.scrollToSelectedTimeSlot();
  }

  private scrollToSelectedTimeSlot(): void {
    setTimeout(() => {
      if (this.currentSelectedSlot) {
        const slotElementId = this.generateHtmlIdForSlot(
          this.currentSelectedSlot
        );

        if (slotElementId) {
          const slotElement = document.getElementById(slotElementId);
          const containerElement = document.getElementById(
            this.SCROLLING_SLOT_CONTAINER_ID
          );

          if (slotElement && containerElement) {
            // if set containerElement.scrollTop = slotElement.offsetTop;
            // the slotElement sill be at the top and hide all previous slots.
            // let it down a little to show partly of previous slot
            const offsetTopPx = 30;

            containerElement.scrollTop = Math.max(
              slotElement.offsetTop - offsetTopPx,
              0
            );
          }
        }
      }
    }, 500);
  }
}
